import {reactive} from 'vue';
import ProductDetails from '../../types/ProductDetails';
import ProductVariation from '../../types/ProductVariation';
import type ProductBoxData from '../../types/ProductBoxData';
import ProductContent from '../../types/ProductContent';
import type TrackingProductData from '../../types/TrackingProductData';
import type RecurringOrderFrequency from '../../types/RecurringOrderFrequency';
import WineLuthierTaste from '../../types/WineLuthierTaste';
import AdsSeoInfo from '../../types/AdsSeoInfo';

class ProductStore {
  protected _adsSeoInfo: AdsSeoInfo;
  protected _productDetails: ProductDetails;
  protected _productSubscription: ProductContent;
  protected _productOrigin: ProductContent;
  protected _productProducer: ProductContent;
  protected _brandlineContent: ProductContent;
  protected _productOriginVintage: ProductContent;
  protected _productGrapes: Array<ProductContent>;
  protected _productVariation: ProductVariation;
  protected _containedInSet: ProductBoxData[];
  protected _trackingData: TrackingProductData;
  protected _selectedQty: number;
  protected _recurringOrderFrequencies: Array<RecurringOrderFrequency>;
  protected _selectedRecurringOrderFrequency: RecurringOrderFrequency;
  protected _selectedAssetType: 'video'|'image';
  protected _upsellQuantity: number;
  protected _customerWinePreferenceKey: string;
  protected _wineLuthierTaste: WineLuthierTaste;
  protected _productAlternativesLength: number;

  protected _getSelectedRecurringOrderFrequencyDefaultValue(productDetails: ProductDetails) {
    return (productDetails.recurringOrderFrequencies && productDetails.recurringOrderFrequencies.length)
      ? productDetails.recurringOrderFrequencies[0]
      : 'UNKNOWN';
  }

  constructor() {
    if (window.__productConfig) {
      this.setData(window.__productConfig.productStore);
    }
  }

  setData(data: ProductStore) {
    this.adsSeoInfo = data.adsSeoInfo;
    this.productDetails = data.productDetails;
    this.productSubscription = data.productSubscription;
    this.productOrigin = data.productOrigin;
    this.productOriginVintage = data.productOriginVintage
    this.productProducer = data.productProducer;
    this.productGrapes = data.productGrapes;
    this.brandlineContent = data.brandlineContent
    this.productVariation = data.productVariation;
    this.containedInSet = data.containedInSet;
    this.trackingData = data.trackingData;
    this.upsellQuantity = data.upsellQuantity;
    this.customerWinePreferenceKey = data.customerWinePreferenceKey;

    this.selectedQty = data.productDetails.product.counter.preselectedQuantity;
    this.recurringOrderFrequencies = data.productDetails.recurringOrderFrequencies;
    this.selectedRecurringOrderFrequency = this._getSelectedRecurringOrderFrequencyDefaultValue(data.productDetails);
    this.selectedAssetType = 'image';
    this.wineLuthierTaste = data.wineLuthierTaste;
    this.productAlternativesLength = data.productAlternativesLength;
  }

  get adsSeoInfo(): AdsSeoInfo {
    return this._adsSeoInfo;
  }

  set adsSeoInfo(data: AdsSeoInfo) {
    this._adsSeoInfo = data;
  }

  get productDetails(): ProductDetails {
    return this._productDetails;
  }

  set productDetails(data: ProductDetails) {
    this._productDetails = data;
  }

  get productSubscription(): ProductContent {
    return this._productSubscription;
  }

  set productSubscription(data: ProductContent) {
    this._productSubscription = data;
  }

  get productOrigin(): ProductContent {
    return this._productOrigin;
  }

  set productOrigin(data: ProductContent) {
    this._productOrigin = data;
  }

  get productProducer(): ProductContent {
    return this._productProducer;
  }

  set productProducer(data: ProductContent) {
    this._productProducer = data;
  }

  get productGrapes(): Array<ProductContent> {
    return this._productGrapes;
  }

  set brandlineContent(data: ProductContent) {
    this._brandlineContent = data
  }

  get brandlineContent(): ProductContent {
    return this._brandlineContent;
  }

  set productOriginVintage(data: ProductContent) {
    this._productOriginVintage = data
  }

  get productOriginVintage(): ProductContent {
    return this._productOriginVintage;
  }

  set productGrapes(data: Array<ProductContent>) {
    this._productGrapes = data;
  }

  get productVariation(): ProductVariation {
    return this._productVariation;
  }

  set productVariation(data: ProductVariation) {
    this._productVariation = data;
  }

  set containedInSet(data: ProductBoxData[]) {
    this._containedInSet = data;
  }

  get containedInSet(): ProductBoxData[] {
    return this._containedInSet;
  }

  set trackingData(data: TrackingProductData) {
    this._trackingData = data;
  }

  get trackingData() {
    return this._trackingData;
  }

  set selectedQty(data: number) {
    this._selectedQty = data;
  }

  get selectedQty() {
    return this._selectedQty;
  }

  set recurringOrderFrequencies(data: Array<RecurringOrderFrequency>) {
    this._recurringOrderFrequencies = data;
  }

  get recurringOrderFrequencies() {
    return this._recurringOrderFrequencies;
  }

  set selectedRecurringOrderFrequency(data: RecurringOrderFrequency) {
    this._selectedRecurringOrderFrequency = data;
  }

  get selectedRecurringOrderFrequency() {
    return this._selectedRecurringOrderFrequency;
  }

  set upsellQuantity(data: number) {
    this._upsellQuantity = data;
  }

  get upsellQuantity() {
    return this._upsellQuantity;
  }

  set selectedAssetType(data: 'video'|'image') {
    this._selectedAssetType = data;
  }

  get selectedAssetType() {
    return this._selectedAssetType;
  }

  set customerWinePreferenceKey(data: string) {
    this._customerWinePreferenceKey = data;
  }

  get customerWinePreferenceKey() {
    return this._customerWinePreferenceKey;
  }

  set wineLuthierTaste(data: WineLuthierTaste) {
    this._wineLuthierTaste = data;
  }

  get wineLuthierTaste() {
    return this._wineLuthierTaste;
  }

  set productAlternativesLength(data: number) {
    this._productAlternativesLength = data;
  }

  get productAlternativesLength() {
    return this._productAlternativesLength;
  }

  addToWishlist() {
    const productDetails = {...this.productDetails};
    productDetails.product.inWishlist = true;
    this.productDetails = productDetails;
  }

  removeFromWishlist() {
    const productDetails = {...this.productDetails};
    productDetails.product.inWishlist = false;
    this.productDetails = productDetails;
  }
}

const productStore: ProductStore = new ProductStore();
export default reactive(productStore);
export {ProductStore};
